import { useState } from 'react';
import ColorPicker, { ColorPresets } from './ColorPicker';
import { Icon } from '../../utils/icons';

const StyleCustomization = ({
    activeElement,
    onBack,
    widgetConfig,
    onWidgetConfigChange
}) => {
    const [activeTab, setActiveTab] = useState('color');

    const renderSlider = (value, onChange, label, min = 0, max = 100, unit = 'px') => (
        <div className="mb-6">
            <label className="text-sm font-medium text-gray-300 mb-3 block">{label}</label>
            <div className="space-y-2">
                <div className="flex items-center space-x-4">
                    <div className="flex-1 relative">
                        <input
                            type="range"
                            min={min}
                            max={max}
                            value={value || min}
                            onChange={(e) => onChange(parseInt(e.target.value))}
                            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 slider"
                            style={{
                                background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${((value || min) - min) / (max - min) * 100}%, #374151 ${((value || min) - min) / (max - min) * 100}%, #374151 100%)`
                            }}
                        />
                        <style>{`
                            .slider::-webkit-slider-thumb {
                                appearance: none;
                                height: 20px;
                                width: 20px;
                                border-radius: 50%;
                                background: #3B82F6;
                                cursor: pointer;
                                border: 2px solid #ffffff;
                                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                            }
                            .slider::-moz-range-thumb {
                                height: 20px;
                                width: 20px;
                                border-radius: 50%;
                                background: #3B82F6;
                                cursor: pointer;
                                border: 2px solid #ffffff;
                                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                            }
                        `}</style>
                    </div>
                    <div className="min-w-[60px] text-right">
                        <span className="text-sm font-mono text-gray-300 bg-gray-800 px-2 py-1 rounded border">
                            {value || min}{unit}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderToggle = (value, onChange, label, disabled = false) => (
        <div className="mb-6">
            <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-300">{label}</label>
                <button
                    onClick={() => !disabled && onChange(!value)}
                    disabled={disabled}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${disabled
                        ? 'bg-gray-600 cursor-not-allowed'
                        : value
                            ? 'bg-blue-600'
                            : 'bg-gray-700'
                        }`}
                    title={disabled ? 'This option is disabled' : `Toggle ${label.toLowerCase()}`}
                >
                    <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${value ? 'translate-x-6' : 'translate-x-1'
                            }`}
                    />
                    {disabled && (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <svg className="w-3 h-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                    )}
                </button>
            </div>
            {disabled && (
                <p className="text-xs text-gray-500 mt-1">This feature is currently disabled</p>
            )}
        </div>
    );

    const renderAlignmentButtons = (value, onChange) => (
        <div className="mb-6">
            <label className="text-sm font-medium text-gray-300 mb-3 block">Alignment</label>
            <div className="flex bg-gray-800 rounded-lg p-1" role="group">
                {[
                    { value: 'left', icon: '⬅️', label: 'Left' },
                    { value: 'center', icon: '↕️', label: 'Center' },
                    { value: 'right', icon: '➡️', label: 'Right' }
                ].map((align) => (
                    <button
                        key={align.value}
                        onClick={() => onChange(align.value)}
                        className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 min-h-[44px] flex items-center justify-center space-x-2 ${value === align.value
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                            }`}
                        title={`Align ${align.label.toLowerCase()}`}
                    >
                        <span className="text-base">{align.icon}</span>
                        <span>{align.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );

    const renderFontSizeControl = (value, onChange, label, fontStyle = {}, onFontStyleChange = null) => (
        <div className="mb-6">
            <label className="text-sm font-medium text-gray-300 mb-3 block">{label}</label>
            <div className="space-y-3">
                {/* Font Size Input */}
                <div className="flex items-center space-x-2 bg-gray-800 rounded-lg px-3 py-2 border border-gray-700 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
                    <span className="text-sm text-gray-400 font-medium">Size</span>
                    <input
                        type="number"
                        value={value || 16}
                        onChange={(e) => onChange(parseInt(e.target.value))}
                        className="w-16 bg-transparent text-white focus:outline-none text-sm font-mono text-center"
                        min="8"
                        max="48"
                        placeholder="16"
                    />
                    <span className="text-sm text-gray-400">px</span>
                </div>

                {/* Font Style Controls */}
                {onFontStyleChange && (
                    <div className="space-y-2">
                        <div className="text-xs font-medium text-gray-400 uppercase tracking-wide">Style</div>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => onFontStyleChange('bold', !fontStyle.bold)}
                                className={`flex-1 flex items-center justify-center px-3 py-2 rounded-lg border transition-all duration-200 font-bold text-sm min-h-[40px] ${fontStyle.bold
                                    ? 'bg-blue-600 border-blue-500 text-white shadow-sm'
                                    : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700 hover:border-gray-600'
                                    }`}
                                title="Toggle Bold"
                            >
                                <span className="mr-2">B</span>
                                <span className="text-xs">Bold</span>
                            </button>
                            <button
                                onClick={() => onFontStyleChange('italic', !fontStyle.italic)}
                                className={`flex-1 flex items-center justify-center px-3 py-2 rounded-lg border transition-all duration-200 italic text-sm min-h-[40px] ${fontStyle.italic
                                    ? 'bg-blue-600 border-blue-500 text-white shadow-sm'
                                    : 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700 hover:border-gray-600'
                                    }`}
                                title="Toggle Italic"
                            >
                                <span className="mr-2">I</span>
                                <span className="text-xs">Italic</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const renderCornerRadiusSelector = (value, onChange) => (
        <div className="mb-6">
            <label className="text-sm font-medium text-gray-300 mb-3 block">Corner Radius</label>
            <div className="grid grid-cols-4 gap-2">
                {[
                    { value: 0, label: 'None', preview: '⬛' },
                    { value: 4, label: 'Small', preview: '▫️' },
                    { value: 8, label: 'Medium', preview: '🔲' },
                    { value: 16, label: 'Large', preview: '🔘' }
                ].map((option) => (
                    <button
                        key={option.value}
                        onClick={() => onChange(option.value)}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg text-xs font-medium transition-all duration-200 min-h-[70px] border-2 ${value === option.value
                            ? 'bg-blue-600 text-white border-blue-400 shadow-lg scale-105'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border-gray-700 hover:border-gray-600'
                            }`}
                        style={{ borderRadius: `${option.value + 4}px` }}
                        title={`${option.label} corner radius (${option.value}px)`}
                    >
                        <span className="text-lg mb-1">{option.preview}</span>
                        <span>{option.label}</span>
                        <span className="text-xs opacity-75">{option.value}px</span>
                    </button>
                ))}
            </div>
        </div>
    );

    const renderBorderRadiusPresets = (value, onChange) => (
        <div className="mb-6">
            <label className="text-sm font-medium text-gray-300 mb-3 block">Edges</label>
            <div className="grid grid-cols-4 gap-2">
                {[
                    { value: 0, label: 'Flat', preview: '⬜' },
                    { value: 8, label: 'Slight', preview: '▫️' },
                    { value: 16, label: 'Round', preview: '🔲' },
                    { value: 24, label: 'Very Round', preview: '🔘' }
                ].map((option) => (
                    <button
                        key={option.value}
                        onClick={() => onChange(option.value)}
                        className={`flex flex-col items-center justify-center p-3 text-xs font-medium transition-all duration-200 min-h-[70px] border-2 ${value === option.value
                            ? 'bg-blue-600 text-white border-blue-400 shadow-lg scale-105'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border-gray-700 hover:border-gray-600'
                            }`}
                        style={{ borderRadius: `${option.value}px` }}
                        title={`${option.label} edges (${option.value}px border radius)`}
                    >
                        <span className="text-lg mb-1">{option.preview}</span>
                        <span>{option.label}</span>
                        <span className="text-xs opacity-75">{option.value}px</span>
                    </button>
                ))}
            </div>
        </div>
    );

    const renderBackgroundScreen = () => (
        <div className="space-y-6">
            {renderToggle(
                widgetConfig.style?.background?.enabled,
                (enabled) => onWidgetConfigChange({
                    ...widgetConfig,
                    style: {
                        ...widgetConfig.style,
                        background: { ...widgetConfig.style?.background, enabled }
                    }
                }),
                'Enable Background'
            )}

            {widgetConfig.style?.background?.enabled && (
                <>
                    {/* Background Type Tabs - Only Color and Gradient */}
                    <div className="mb-6">
                        <label className="text-sm font-medium text-gray-300 mb-3 block">Background Type</label>
                        <div className="flex bg-gray-800 rounded-lg p-1" role="tablist">
                            {['color', 'gradient'].map((type) => (
                                <button
                                    key={type}
                                    onClick={() => setActiveTab(type)}
                                    role="tab"
                                    aria-selected={activeTab === type}
                                    className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === type
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-gray-300 hover:text-white hover:bg-gray-700'
                                        }`}
                                >
                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Color Tab */}
                    {activeTab === 'color' && (
                        <div className="space-y-6">
                            <ColorPicker
                                value={widgetConfig.style?.background?.color}
                                onChange={(color) => onWidgetConfigChange({
                                    ...widgetConfig,
                                    style: {
                                        ...widgetConfig.style,
                                        background: {
                                            ...widgetConfig.style?.background,
                                            color
                                        }
                                    }
                                })}
                                label="Background Color"
                                showHex={true}
                            />

                            {/* Quick Color Presets */}
                            <ColorPresets
                                selectedColor={widgetConfig.style?.background?.color}
                                onColorSelect={(color) => onWidgetConfigChange({
                                    ...widgetConfig,
                                    style: {
                                        ...widgetConfig.style,
                                        background: {
                                            ...widgetConfig.style?.background,
                                            color
                                        }
                                    }
                                })}
                                className="mt-4"
                            />

                            {/* Container Styling Options */}
                            <div className="mt-8 pt-6 border-t border-gray-700">
                                <h4 className="text-sm font-medium text-gray-300 mb-4">Container Styling</h4>

                                {/* Width Slider */}
                                {renderSlider(
                                    widgetConfig.style?.background?.width || 1200,
                                    (width) => onWidgetConfigChange({
                                        ...widgetConfig,
                                        style: {
                                            ...widgetConfig.style,
                                            background: {
                                                ...widgetConfig.style?.background,
                                                width
                                            }
                                        }
                                    }),
                                    'Container Width',
                                    320,
                                    1200,
                                    'px'
                                )}

                                {/* Border Radius Presets */}
                                {renderBorderRadiusPresets(
                                    widgetConfig.style?.background?.borderRadius ?? 16,
                                    (borderRadius) => onWidgetConfigChange({
                                        ...widgetConfig,
                                        style: {
                                            ...widgetConfig.style,
                                            background: {
                                                ...widgetConfig.style?.background,
                                                borderRadius
                                            }
                                        }
                                    })
                                )}

                                {/* Shadow Toggle */}
                                {renderToggle(
                                    widgetConfig.style?.background?.shadow ?? true,
                                    (shadow) => onWidgetConfigChange({
                                        ...widgetConfig,
                                        style: {
                                            ...widgetConfig.style,
                                            background: {
                                                ...widgetConfig.style?.background,
                                                shadow
                                            }
                                        }
                                    }),
                                    'Container Shadow'
                                )}
                            </div>
                        </div>
                    )}

                    {/* Gradient Tab */}
                    {activeTab === 'gradient' && (
                        <div className="space-y-6">
                            <div className="text-center py-8 bg-gray-800 rounded-lg border border-gray-700">
                                <Icon name="palette" size={48} className="text-gray-400 mb-2" />
                                <h4 className="text-sm font-medium text-gray-300 mb-2">Gradient Options</h4>
                                <p className="text-xs text-gray-500">Advanced gradient controls coming soon</p>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    const renderWidgetTitleScreen = () => (
        <div className="space-y-8">
            {/* Alignment Section */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-4 flex items-center">
                    <Icon name="location" size={16} className="w-6 h-6 bg-blue-600 rounded-lg flex items-center justify-center mr-3 text-white" />
                    Alignment
                </h4>
                {renderAlignmentButtons(
                    widgetConfig.style?.widgetTitle?.alignment || 'center',
                    (alignment) => onWidgetConfigChange({
                        ...widgetConfig,
                        style: {
                            ...widgetConfig.style,
                            widgetTitle: { ...widgetConfig.style?.widgetTitle, alignment }
                        }
                    })
                )}
            </div>

            {/* Colors Section */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-4 flex items-center">
                    <Icon name="palette" size={16} className="w-6 h-6 bg-purple-600 rounded-lg flex items-center justify-center mr-3 text-white" />
                    Colors
                </h4>
                <div className="space-y-6 bg-gray-800/50 rounded-lg p-4">
                    <ColorPicker
                        value={widgetConfig.style?.widgetTitle?.titleColor}
                        onChange={(titleColor) => onWidgetConfigChange({
                            ...widgetConfig,
                            style: {
                                ...widgetConfig.style,
                                widgetTitle: { ...widgetConfig.style?.widgetTitle, titleColor }
                            }
                        })}
                        label="Title Color"
                    />

                    <ColorPicker
                        value={widgetConfig.style?.widgetTitle?.captionColor}
                        onChange={(captionColor) => onWidgetConfigChange({
                            ...widgetConfig,
                            style: {
                                ...widgetConfig.style,
                                widgetTitle: { ...widgetConfig.style?.widgetTitle, captionColor }
                            }
                        })}
                        label="Caption Color"
                    />
                </div>
            </div>

            {/* Typography Section */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-4 flex items-center">
                    <span className="w-6 h-6 bg-green-600 rounded-lg flex items-center justify-center mr-3 text-xs">Aa</span>
                    Typography
                </h4>
                <div className="space-y-6 bg-gray-800/50 rounded-lg p-4">
                    {renderFontSizeControl(
                        widgetConfig.style?.widgetTitle?.titleSize || 24,
                        (titleSize) => onWidgetConfigChange({
                            ...widgetConfig,
                            style: {
                                ...widgetConfig.style,
                                widgetTitle: { ...widgetConfig.style?.widgetTitle, titleSize }
                            }
                        }),
                        'Title Font',
                        {
                            bold: widgetConfig.style?.widgetTitle?.titleBold || false,
                            italic: widgetConfig.style?.widgetTitle?.titleItalic || false
                        },
                        (property, value) => onWidgetConfigChange({
                            ...widgetConfig,
                            style: {
                                ...widgetConfig.style,
                                widgetTitle: {
                                    ...widgetConfig.style?.widgetTitle,
                                    [`title${property.charAt(0).toUpperCase() + property.slice(1)}`]: value
                                }
                            }
                        })
                    )}

                    {renderFontSizeControl(
                        widgetConfig.style?.widgetTitle?.captionSize || 15,
                        (captionSize) => onWidgetConfigChange({
                            ...widgetConfig,
                            style: {
                                ...widgetConfig.style,
                                widgetTitle: { ...widgetConfig.style?.widgetTitle, captionSize }
                            }
                        }),
                        'Caption Font',
                        {
                            bold: widgetConfig.style?.widgetTitle?.captionBold || false,
                            italic: widgetConfig.style?.widgetTitle?.captionItalic || false
                        },
                        (property, value) => onWidgetConfigChange({
                            ...widgetConfig,
                            style: {
                                ...widgetConfig.style,
                                widgetTitle: {
                                    ...widgetConfig.style?.widgetTitle,
                                    [`caption${property.charAt(0).toUpperCase() + property.slice(1)}`]: value
                                }
                            }
                        })
                    )}
                </div>
            </div>
        </div>
    );

    const renderRatingScreen = () => (
        <div className="space-y-6">
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-4">Star Appearance</h4>
                <ColorPicker
                    value={widgetConfig.style?.rating?.starsColor}
                    onChange={(starsColor) => onWidgetConfigChange({
                        ...widgetConfig,
                        style: {
                            ...widgetConfig.style,
                            rating: { ...widgetConfig.style?.rating, starsColor }
                        }
                    })}
                    label="Stars Color"
                />
            </div>
        </div>
    );

    const renderReviewScreen = () => (
        <div className="space-y-6">
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-4">Color Options</h4>
                <div className="space-y-6">
                    <ColorPicker
                        value={widgetConfig.style?.review?.backgroundColor}
                        onChange={(backgroundColor) => onWidgetConfigChange({
                            ...widgetConfig,
                            style: {
                                ...widgetConfig.style,
                                review: { ...widgetConfig.style?.review, backgroundColor }
                            }
                        })}
                        label="Background Color"
                    />

                    <ColorPicker
                        value={widgetConfig.style?.review?.outlineColor}
                        onChange={(outlineColor) => onWidgetConfigChange({
                            ...widgetConfig,
                            style: {
                                ...widgetConfig.style,
                                review: { ...widgetConfig.style?.review, outlineColor }
                            }
                        })}
                        label="Review Outline Color"
                    />

                    <ColorPicker
                        value={widgetConfig.style?.review?.textColor}
                        onChange={(textColor) => onWidgetConfigChange({
                            ...widgetConfig,
                            style: {
                                ...widgetConfig.style,
                                review: { ...widgetConfig.style?.review, textColor }
                            }
                        })}
                        label="Review Text Color"
                    />

                    <ColorPicker
                        value={widgetConfig.style?.review?.reviewerNameColor}
                        onChange={(reviewerNameColor) => onWidgetConfigChange({
                            ...widgetConfig,
                            style: {
                                ...widgetConfig.style,
                                review: { ...widgetConfig.style?.review, reviewerNameColor }
                            }
                        })}
                        label="Reviewer Name Color"
                    />

                    <ColorPicker
                        value={widgetConfig.style?.review?.dateColor}
                        onChange={(dateColor) => onWidgetConfigChange({
                            ...widgetConfig,
                            style: {
                                ...widgetConfig.style,
                                review: { ...widgetConfig.style?.review, dateColor }
                            }
                        })}
                        label="Date Color"
                    />
                </div>
            </div>

            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-4">Text Settings</h4>
                <div className="space-y-4">
                    {renderFontSizeControl(
                        widgetConfig.style?.review?.textSize || 16,
                        (textSize) => onWidgetConfigChange({
                            ...widgetConfig,
                            style: {
                                ...widgetConfig.style,
                                review: { ...widgetConfig.style?.review, textSize }
                            }
                        }),
                        'Review Text Font Size'
                    )}
                </div>
            </div>

            {renderCornerRadiusSelector(
                widgetConfig.style?.review?.cornerRadius || 8,
                (cornerRadius) => onWidgetConfigChange({
                    ...widgetConfig,
                    style: {
                        ...widgetConfig.style,
                        review: { ...widgetConfig.style?.review, cornerRadius }
                    }
                })
            )}
        </div>
    );

    const renderPaginationScreen = () => (
        <div className="space-y-6">
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-4">Pagination Dots</h4>
                <ColorPicker
                    value={widgetConfig.style?.pagination?.color}
                    onChange={(color) => onWidgetConfigChange({
                        ...widgetConfig,
                        style: {
                            ...widgetConfig.style,
                            pagination: { ...widgetConfig.style?.pagination, color }
                        }
                    })}
                    label="Pagination Color"
                />
            </div>
        </div>
    );

    const getElementTitle = () => {
        const titles = {
            background: 'Background',
            widgetTitle: 'Widget Title',
            rating: 'Rating',
            review: 'Review',
            pagination: 'Pagination'
        };
        return titles[activeElement] || 'Customize';
    };

    const renderContent = () => {
        switch (activeElement) {
            case 'background':
                return renderBackgroundScreen();
            case 'widgetTitle':
                return renderWidgetTitleScreen();
            case 'rating':
                return renderRatingScreen();
            case 'review':
                return renderReviewScreen();
            case 'pagination':
                return renderPaginationScreen();
            default:
                return <div className="text-center py-8 text-gray-400">Select an element to customize</div>;
        }
    };

    return (
        <div className="h-full flex flex-col">
            {/* Header - Fixed at 64px height for consistency */}
            <div className="h-16 flex items-center justify-between px-6 border-b border-gray-700 bg-gray-900">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={onBack}
                        className="flex items-center justify-center w-10 h-10 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        title="Go back"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h3 className="text-lg font-semibold text-white">{getElementTitle()}</h3>
                </div>
                <button
                    onClick={onBack}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 min-h-[44px]"
                >
                    Done
                </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6">
                {renderContent()}
            </div>
        </div>
    );
};

export default StyleCustomization; 